import { useMutation, useQuery } from "@apollo/client";
import { UPDATE_CUSTOMER_MARKETING_CONSENT } from "@/domains/customer/consent/graphql/consent-queries";
import { gql } from "@/gql";

// GraphQL queries and mutations for marketing consent
const GET_CUSTOMER_MARKETING_CONSENTS = gql(`
  query GetCustomerMarketingConsents($customerId: String!) {
    getCustomerConsents(customerId: $customerId) {
      id
      consentType
      consentChannel
      isGranted
      isActive
      grantedAt
      revokedAt
    }
  }
`);

// Using centralized mutation from consent-queries

interface UseMarketingConsentOptions {
  customerId: string;
}

export const useMarketingConsent = ({ customerId }: UseMarketingConsentOptions) => {
  // Query existing consent - use GraphQL data directly
  const { data, loading, error, refetch } = useQuery(GET_CUSTOMER_MARKETING_CONSENTS, {
    variables: { customerId },
    skip: !customerId,
  });

  // Process GraphQL data directly without local state
  const consents = data?.getCustomerConsents || [];
  const emailConsent = consents.find(
    (c) => c.consentType === "MARKETING" && c.consentChannel === "EMAIL"
  );
  const smsConsent = consents.find(
    (c) => c.consentType === "MARKETING" && c.consentChannel === "SMS"
  );

  const emailGranted = Boolean(emailConsent?.isGranted && emailConsent?.isActive);
  const smsGranted = Boolean(smsConsent?.isGranted && smsConsent?.isActive);

  const marketingConsent = {
    email: emailGranted,
    sms: smsGranted,
  };

  // Mutation for updating consent
  const [updateMarketingConsentMutation, { loading: isUpdating }] = useMutation(
    UPDATE_CUSTOMER_MARKETING_CONSENT,
    {
      refetchQueries: [
        {
          query: GET_CUSTOMER_MARKETING_CONSENTS,
          variables: { customerId },
        },
      ],
      // Let GraphQL cache handle the updates automatically
    }
  );

  // Update both email and SMS consent (simple - single API call)
  const updateBothMarketingConsents = async (hasMarketingConsent: boolean) => {
    try {
      await updateMarketingConsentMutation({
        variables: {
          customerId,
          hasMarketingConsent,
        },
      });
    } catch (error) {
      console.error("Failed to update marketing consents:", error);
      throw error;
    }
  };

  // Get overall marketing consent status (true if either email or SMS is granted)
  const hasMarketingConsent = marketingConsent.email || marketingConsent.sms;

  // Get overall marketing consent status (true only if both email and SMS are granted)
  const hasBothMarketingConsents = marketingConsent.email && marketingConsent.sms;

  return {
    // State
    marketingConsent,
    hasMarketingConsent,
    hasBothMarketingConsents,

    // Loading states
    loading,
    isUpdating,

    // Error state
    error,

    // Actions
    updateBothMarketingConsents,
    refetch,
  };
};
